import type { Blatt as BlattName, VorschauSchalter } from "../hooks/useVertragstext";
import { BLATT_LABEL, fehlertext, useVorschau } from "../hooks/useVertragstext";
import VertragAnsicht from "./VertragAnsicht";

/**
 * Live-Vorschau des Vertragstextes am Beispielkunden.
 *
 * Sie ersetzt das, was man vorher tun musste: einen Testvertrag anlegen, das
 * PDF erzeugen und hineinsehen. Gerendert wird auf dem Server mit demselben
 * Renderer, der den echten Vertrag baut — eine Nachbildung im Frontend würde
 * driften, und zwar unbemerkt, weil sie ja „nur die Vorschau" wäre.
 *
 * Der Text landet in einem Schatten-DOM. Das Vertrags-Stylesheet ist für ein
 * DIN-A4-Blatt gemacht und würde sonst über die ganze App laufen: es setzt
 * Seitenränder, Schriftgrößen und Tabellen um.
 */
export default function VertragVorschau({
  werkId, blatt = "urkunde", schalter, onSchalter,
}: {
  werkId: string | null;
  /** Welches Blatt gezeigt wird — dasselbe, das im Editor offen ist. */
  blatt?: BlattName;
  schalter: VorschauSchalter;
  onSchalter: (s: VorschauSchalter) => void;
}) {
  const { data, isLoading, error } = useVorschau(werkId, schalter);
  const setzen = (teil: Partial<VorschauSchalter>) => onSchalter({ ...schalter, ...teil });

  const html = !data ? "" :
    blatt === "agb" ? data.agb_html :
    blatt === "anlage" ? data.anlage_html :
    blatt === "mandat" ? data.mandat_html :
    blatt === "bestand" ? data.bestand_html :
    data.paragrafen_html + data.anhaenge_html;

  return (
    <div className="vt-vorschau">
      <div className="vt-schalter">
        <div className="vt-schalter-zeile">
          <select className="pf-input is-tight" value={schalter.vertragsart}
                  onChange={e => setzen({ vertragsart: e.target.value })}>
            <option value="verbraucher">Verbraucher</option>
            <option value="unternehmer">Gewerbe</option>
          </select>
          <select className="pf-input is-tight" value={schalter.zahlungsart}
                  onChange={e => setzen({ zahlungsart: e.target.value })}>
            <option value="ueberweisung">Überweisung</option>
            <option value="lastschrift">Lastschrift</option>
          </select>
        </div>
        <div className="vt-schalter-flags">
          <Flag an={schalter.je_einsatz} setzen={v => setzen({ je_einsatz: v })}
                text="je Einsatz statt Rate" />
          <Flag an={schalter.zahlweise_offen} setzen={v => setzen({ zahlweise_offen: v })}
                text="Zahlweise noch offen" />
          <Flag an={schalter.ohne_zugang} setzen={v => setzen({ ohne_zugang: v })}
                text="ohne Zugangshinweis" />
          <Flag an={schalter.ohne_sondervereinbarung}
                setzen={v => setzen({ ohne_sondervereinbarung: v })}
                text="ohne Sondervereinbarung" />
          <Flag an={schalter.ohne_bestand} setzen={v => setzen({ ohne_bestand: v })}
                text="ohne Bestandsaufnahme" />
          <Flag an={schalter.ohne_abruf} setzen={v => setzen({ ohne_abruf: v })}
                text="ohne Abrufleistungen" />
        </div>
      </div>

      {error ? (
        <div className="vt-fehler">
          <strong>Der Text rendert in dieser Variante nicht.</strong>
          <div>{fehlertext(error)}</div>
          <div className="vt-hint">
            Solange das so ist, lässt sich die Fassung nicht freigeben — der Fehler
            steckt in einem Platzhalter oder einem Verweis, nicht im Server.
          </div>
        </div>
      ) : isLoading && !data ? (
        <div className="pf-empty">Rendere…</div>
      ) : data ? (
        <>
          <Seitenzahl blatt={blatt} seiten={data.seiten} />
          <VertragAnsicht css={data.css} html={html} bilder={data.seiten_bilder?.[blatt]} />
        </>
      ) : null}
    </div>
  );
}

/**
 * Wie viele Seiten dieses Blatt hat — gemessen, nicht geschätzt.
 *
 * Die drei Seiten der Urkunde sind der Grund für den ganzen Umbau; sie
 * verdienen eine Anzeige und keine Erinnerung. Ohne sie ist die Zusage nach
 * der dritten Textänderung still wieder weg.
 *
 * **Kein Riegel.** Ein Garten mit dreißig Positionen braucht vier Seiten, und
 * dann ist das richtig. Die Anzeige sagt, was ist; entschieden wird von Hand.
 */
function Seitenzahl({ blatt, seiten }: {
  blatt: BlattName;
  seiten: Record<string, number | undefined>;
}) {
  const n = seiten?.[blatt];
  if (!n) return null;
  const knapp = blatt === "urkunde" && n > 3;
  return (
    <div className={"vt-seitenzahl" + (knapp ? " is-warn" : "")}>
      <strong>{BLATT_LABEL[blatt]}: {n} {n === 1 ? "Seite" : "Seiten"}</strong>
      {blatt === "urkunde" && (knapp
        ? " — zugesagt sind höchstens drei. Was hier nicht Geld, Leistung, "
          + "Laufzeit oder Kündigung ist, gehört in die AGB."
        : " von höchstens drei.")}
      {seiten.widerruf ? ` · Widerrufsbelehrung: ${seiten.widerruf}` : ""}
    </div>
  );
}

function Flag({ an, setzen, text }: {
  an: boolean; setzen: (v: boolean) => void; text: string;
}) {
  return (
    <label className={"vt-flag" + (an ? " is-on" : "")}>
      <input type="checkbox" checked={an} onChange={e => setzen(e.target.checked)} />
      <span>{text}</span>
    </label>
  );
}
